import { CalibrationDisplaySchema } from "../types";

/**
 * Accelerometer calibration display schema.
 *
 * Accelerometer calibrations are submitted exclusively by the NexaWave Utility
 * desktop app after physical 6-face gravity calibration + 9-point static test.
 * There is no manual input form — inputFieldConfig is intentionally omitted.
 *
 * This schema only configures how submitted data is displayed (inputData /
 * outputData) and how the certificate form looks.
 */
export const AccelerometerSchema: CalibrationDisplaySchema = {
  sensorType: "mems",
  unit: "g",

  // No inputFieldConfig — calibration cannot be started from the frontend.

  certificateFormConfig: {
    title: "Accelerometer Certificate Details",
    fields: [
      {
        key: "date",
        label: "Date",
        type: "date",
        placeholder: "DD.MM.YYYY",
      },
      {
        key: "temperature",
        label: "Temperature",
        type: "text",
        placeholder: "e.g., 26 °C",
        unit: "°C",
      },
      {
        key: "humidity",
        label: "Humidity",
        type: "text",
        placeholder: "e.g., 58 %",
        unit: "%",
      },
    ],
  },

  inputData: {
    sections: [
      {
        title: "Test Configuration",
        bgColor: "bg-blue-50",
        group: "setup",
        fields: [
          { key: "g_range", label: "Measurement Range", unit: "g" },
          { key: "temperature", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
          { key: "firmware_version", label: "Firmware Version", unit: "" },
          { key: "device_model", label: "Device Model", unit: "" },
        ],
      },
      {
        title: "Calibration Coefficients",
        bgColor: "bg-indigo-50",
        group: "setup",
        fields: [
          {
            key: "calibration_coefficients.offset_x",
            label: "Offset X",
            unit: "g",
          },
          {
            key: "calibration_coefficients.offset_y",
            label: "Offset Y",
            unit: "g",
          },
          {
            key: "calibration_coefficients.offset_z",
            label: "Offset Z",
            unit: "g",
          },
          {
            key: "calibration_coefficients.gauge_factor_x",
            label: "Gauge Factor X",
            unit: "",
          },
          {
            key: "calibration_coefficients.gauge_factor_y",
            label: "Gauge Factor Y",
            unit: "",
          },
          {
            key: "calibration_coefficients.gauge_factor_z",
            label: "Gauge Factor Z",
            unit: "",
          },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "calibration_coefficients" in data,
      },
    ],

    tables: [
      {
        title: "9-Point Static Acceleration Test",
        bgColor: "bg-green-50",
        group: "measurement",
        dataKey: "test_report.test_results",
        columns: [
          { key: "axis", label: "Axis", unit: "" },
          { key: "range_g", label: "Range", unit: "" },
          {
            key: "reference_g",
            label: "Reference (g)",
            unit: "",
            preserveLabelCase: true,
          },
          {
            key: "measured_g",
            label: "Measured (g)",
            unit: "",
            className: "font-mono",
            preserveLabelCase: true,
          },
          {
            key: "error_percent_fs",
            label: "Error (%FS)",
            unit: "%",
            className: "font-mono",
          },
          { key: "pass_fail", label: "P/F", unit: "" },
        ],
        rowClassName: (row: unknown) =>
          typeof row === "object" &&
          row !== null &&
          "pass_fail" in row &&
          (row as any).pass_fail === "FAIL"
            ? "bg-red-50"
            : "",
        // No `condition` here — dataKey resolution above already hides the
        // table when there's no data.
      },
    ],
  },

  outputData: {
    sections: [
      {
        title: "Summary",
        bgColor: "bg-green-50",
        group: "result",
        fields: [
          { key: "calibration_status", label: "Calibration Status", unit: "" },
          { key: "g_range", label: "Measurement Range", unit: "g" },
          {
            key: "max_error_percent_fs",
            label: "Max Error %FS",
            unit: "%",
          },
          { key: "tolerance_percent", label: "Tolerance", unit: "%" },
          { key: "temperature", label: "Temperature", unit: "°C" },
          { key: "humidity", label: "Humidity", unit: "%" },
        ],
      },
      {
        title: "Calibration Coefficients",
        bgColor: "bg-indigo-50",
        group: "result",
        fields: [
          { key: "offset_x", label: "Offset X", unit: "g" },
          { key: "offset_y", label: "Offset Y", unit: "g" },
          { key: "offset_z", label: "Offset Z", unit: "g" },
          { key: "gauge_factor_x", label: "Gauge Factor X", unit: "" },
          { key: "gauge_factor_y", label: "Gauge Factor Y", unit: "" },
          { key: "gauge_factor_z", label: "Gauge Factor Z", unit: "" },
        ],
        condition: (data: unknown) =>
          typeof data === "object" &&
          data !== null &&
          "calibration_coefficients" in data,
      },
    ],

    tables: [
      {
        // Computed/verified test results attached to the calibration result
        // record — the axis-by-axis error and pass/fail verdict that backs
        // the calibration's Analysis view.
        title: "9-Point Test Results",
        bgColor: "bg-red-50",
        group: "analysis",
        dataKey: "test_results",
        columns: [
          { key: "axis", label: "Axis", unit: "" },
          {
            key: "reference_g",
            label: "Reference (g)",
            unit: "",
            className: "font-medium",
            preserveLabelCase: true,
          },
          {
            key: "measured_g",
            label: "Measured (g)",
            unit: "",
            className: "font-mono",
            preserveLabelCase: true,
          },
          {
            key: "error_percent_fs",
            label: "Error (%FS)",
            unit: "%",
            className: "font-mono",
          },
          { key: "pass_fail", label: "P/F", unit: "" },
          { key: "orientation", label: "Orientation", unit: "" },
        ],
        rowClassName: (row: unknown) =>
          typeof row === "object" &&
          row !== null &&
          "pass_fail" in row &&
          (row as any).pass_fail === "FAIL"
            ? "bg-red-50"
            : "",
        // No `condition` here — dataKey resolution above already hides the
        // table when there's no data.
      },
    ],
  },
};
